import React, { useState } from "react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Divider,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  FormControl,
  InputLabel,
} from "@mui/material";
import * as XLSX from "xlsx";

const normalizeKey = (key) =>
  String(key || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const parseDateWithError = (value) => {
  if (value === null || value === undefined || value === "") {
    return { iso: "", error: "" };
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return { iso: value.toISOString().slice(0, 10), error: "" };
  }
  if (typeof value === "number") {
    const date = new Date(Math.round((value - 25569) * 86400 * 1000));
    return Number.isNaN(date.getTime())
      ? { iso: "", error: "Invalid Excel date number" }
      : { iso: date.toISOString().slice(0, 10), error: "" };
  }
  const raw = String(value).trim();
  if (!raw) return { iso: "", error: "" };
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return { iso: raw, error: "" };

  const simpleMatch = raw.match(/^(\d{1,2})[\/.\-](\d{1,2})[\/.\-](\d{2,4})$/);
  if (simpleMatch) {
    let [, p1, p2, y] = simpleMatch;
    if (y.length === 2) y = `20${y}`;
    if (y.length === 3) y = `20${y}`;
    let d = p1;
    let m = p2;
    if (Number(p1) > 12) {
      d = p1;
      m = p2;
    } else if (Number(p2) > 12) {
      d = p2;
      m = p1;
    } else {
      d = p2;
      m = p1;
    }
    const dd = String(d).padStart(2, "0");
    const mm = String(m).padStart(2, "0");
    const iso = `${y}-${mm}-${dd}`;
    const check = new Date(iso);
    return Number.isNaN(check.getTime())
      ? { iso: "", error: "Invalid date value" }
      : { iso, error: "" };
  }

  return {
    iso: "",
    error: "Invalid date format. Use DD/MM/YYY or MM/DD/YYY.",
  };
};

const DataQualityPage = () => {
  const [dataset, setDataset] = useState("enquiry");
  const [errors, setErrors] = useState([]);
  const [fileName, setFileName] = useState("");

  const handleFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: "array" });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
    const issues = [];

    rows.forEach((row, index) => {
      const rowIndex = index + 2; // 1-based + header row
      const get = (...keys) => {
        for (const key of keys) {
          const normalized = normalizeKey(key);
          for (const rawKey of Object.keys(row)) {
            if (normalizeKey(rawKey) === normalized) {
              return row[rawKey];
            }
          }
        }
        return "";
      };

      if (dataset === "enquiry") {
        const enquiryNo = get("enquiryno", "enquirycase", "enquirycaseno", "enquirycasenumber", "enquiry/caseno");
        const complainant = get("complainantname", "nameofcomplainant", "complainant name");
        const category = get("categorytype", "category type");
        if (!enquiryNo) {
          issues.push({ row: rowIndex, column: "Enquiry Case No", message: "Required field missing" });
        }
        if (!complainant) {
          issues.push({ row: rowIndex, column: "Complainant Name", message: "Required field missing" });
        }
        if (!category) {
          issues.push({ row: rowIndex, column: "Category Type", message: "Required field missing" });
        }

        const dateOfReg = get("dateofreg", "date of reg", "dateofregistration");
        const receivingDate = get("receivingdate", "receiving date");
        const dateErrors = [
          { key: "Date Of Reg", value: dateOfReg },
          { key: "Receiving Date", value: receivingDate },
        ];
        dateErrors.forEach((item) => {
          if (!item.value) return;
          const parsed = parseDateWithError(item.value);
          if (parsed.error) {
            issues.push({ row: rowIndex, column: item.key, message: parsed.error });
          }
        });
      } else {
        const enquiryCaseNum = get("enquirycasenum", "enquirycase", "enquirycaseno", "enquiry/caseno");
        const name = get("name", "cname", "alleged name");
        const address = get("address");
        const complainant = get("complainantname", "complainant name");
        const gist = get("gistofallegations", "gistofallegation", "gist");
        if (!enquiryCaseNum) {
          issues.push({ row: rowIndex, column: "Enquiry Case Number", message: "Required field missing" });
        }
        if (!name) {
          issues.push({ row: rowIndex, column: "Name", message: "Required field missing" });
        }
        if (!address) {
          issues.push({ row: rowIndex, column: "Address", message: "Required field missing" });
        }
        if (!complainant) {
          issues.push({ row: rowIndex, column: "Complainant Name", message: "Required field missing" });
        }
        if (!gist) {
          issues.push({ row: rowIndex, column: "Gist Of Allegations", message: "Required field missing" });
        }

        const dateFields = [
          { key: "Notice Dispatch Date", value: get("noticedispatchdate", "notice dispatch date", "notice1dispatchdate") },
          { key: "Appearance Date", value: get("appearencedate", "appearance date", "notice1appearancedate") },
          { key: "2nd Notice", value: get("notice2dispatchdate", "2ndnotice") },
          { key: "2nd Appearance", value: get("notice2appearancedate", "2ndnoticeappearance") },
          { key: "3rd Notice", value: get("notice3dispatchdate", "3rdnotice") },
          { key: "3rd Appearance", value: get("notice3appearancedate", "3rdnoticeappearance") },
          { key: "DPO Dispatch", value: get("dpoletterdispatchdate", "dpo dispatch") },
          { key: "DPO Appearance", value: get("dpoletterappearancedate", "dpo appearance") },
        ];
        dateFields.forEach((item) => {
          if (!item.value) return;
          const parsed = parseDateWithError(item.value);
          if (parsed.error) {
            issues.push({ row: rowIndex, column: item.key, message: parsed.error });
          }
        });
      }
    });

    setErrors(issues);
  };

  const handleExportErrors = () => {
    if (!errors.length) return;
    const worksheet = XLSX.utils.json_to_sheet(errors);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Errors");
    XLSX.writeFile(workbook, "data-quality-errors.xlsx");
  };

  return (
    <Container maxWidth="lg">
      <Stack spacing={3}>
        <Card elevation={1}>
          <CardHeader title="Data Quality Report" subheader="Validate Excel/CSV before import" />
          <CardContent>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }}>
              <FormControl sx={{ minWidth: 220 }}>
                <InputLabel>Dataset</InputLabel>
                <Select
                  value={dataset}
                  label="Dataset"
                  onChange={(e) => setDataset(e.target.value)}
                >
                  <MenuItem value="enquiry">Enquiry Case Details</MenuItem>
                  <MenuItem value="summon">Summon Notices</MenuItem>
                </Select>
              </FormControl>
              <Button variant="contained" component="label">
                Upload File
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
              </Button>
              <Button variant="outlined" onClick={handleExportErrors} disabled={!errors.length}>
                Export Errors
              </Button>
              {fileName && (
                <Typography variant="body2" color="text.secondary">
                  {fileName}
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card elevation={1}>
          <CardHeader
            title="Validation Results"
            subheader={errors.length ? `${errors.length} issue(s) found` : "No issues detected"}
          />
          <Divider />
          <CardContent>
            {errors.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Upload a file to see validation issues.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Column</TableCell>
                      <TableCell>Issue</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {errors.map((err, idx) => (
                      <TableRow key={`${err.row}-${err.column}-${idx}`}>
                        <TableCell>{err.row}</TableCell>
                        <TableCell>{err.column}</TableCell>
                        <TableCell>{err.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
};

export default DataQualityPage;
